import React, { FormEvent, MouseEvent, useEffect, useState } from 'react';
import { Button, InputAdornment, styled, TextField } from '@mui/material';
import Dialog from 'components/dialogs/Dialog';
import { SupplierProduct } from 'types/supplier';
import { Product } from 'types/product';
import MoneyInput from 'components/masked-input/MoneyInput';
import { toast } from 'react-toastify';
import ProductsAutoComplete from 'components/autocomplete/ProductsAutoComplete';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface SupplierProductFormProps {
  onExited: () => void;
  handleAddProduct: (productInfo: SupplierProduct | null) => void;
  productInfo: SupplierProduct | null;
  handleChangeProductInfo(index: keyof SupplierProduct, value: any): void;
  type: 'new' | 'edit';
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  columnGap: 10,
  rowGap: 15,
});

const DialogActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
});

const SupplierProductForm: React.FC<SupplierProductFormProps> = ({
  handleAddProduct,
  onExited,
  productInfo,
  handleChangeProductInfo,
  type,
}) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [validation, setValidation] = useState('');

  useEffect(() => {
    setProduct({
      id: productInfo?.product_id || 0,
      name: productInfo?.product_name || '',
      unit: productInfo?.product_unit || '',
      manufacturer: productInfo?.product_manufacturer || '',
    });
  }, [productInfo]);

  function handleValidation(e?: MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    if (!product || !productInfo) {
      toast.error('Selecione um produto');
      return;
    }

    if (!productInfo.cost_price || !productInfo.product_name) {
      setValidation(!productInfo.cost_price ? 'cost_price' : 'product_name');
      return;
    }

    if (!productInfo.sale_price_min || !productInfo.sale_price_max) {
      setValidation(!productInfo.sale_price_min ? 'sale_price_min' : 'sale_price_max');
      return;
    }

    handleAddProduct({
      ...productInfo,
      product_name: product?.name || '',
      product_id: product?.id || 0,
      product_unit: product?.unit || '',
      product_manufacturer: product?.manufacturer || '',
    });
  }
  return (
    <Dialog
      onExited={onExited}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      title={type === 'edit' ? 'Editar produto' : 'Adicionar produto'}
    >
      <Container>
        <ProductsAutoComplete
          label="Produto"
          onSelect={product => {
            handleChangeProductInfo('product_name', product?.name || '');
            handleChangeProductInfo('product_id', product?.id || '');
            handleChangeProductInfo('product_unit', product?.unit || '');
            handleChangeProductInfo('product_manufacturer', product?.manufacturer || '');
            setProduct(product);
          }}
          placeholder="Selecione um produto"
          selected={product}
        />

        <TextField
          error={validation === 'cost_price'}
          helperText={validation === 'cost_price' ? 'Preço de custo é obrigatório' : ''}
          label="Preço de custo"
          placeholder="Digite o preço de custo"
          value={productInfo?.cost_price || 0}
          onChange={e => handleChangeProductInfo('cost_price', e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              inputComponent: asInputComponent(MoneyInput),
            },
          }}
        />

        <TextField
          error={validation === 'sale_price_min'}
          helperText={validation === 'sale_price_min' ? 'Preço de venda mínimo é obrigatório' : ''}
          label="Preço de venda mínimo"
          placeholder="Digite o preço de venda mínimo"
          value={productInfo?.sale_price_min || 0}
          onChange={e => handleChangeProductInfo('sale_price_min', e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              inputComponent: asInputComponent(MoneyInput),
            },
          }}
        />

        <TextField
          error={validation === 'sale_price_max'}
          helperText={validation === 'sale_price_max' ? 'Preço de venda máximo é obrigatório' : ''}
          label="Preço de venda máximo"
          placeholder="Digite o preço de venda máximo"
          value={productInfo?.sale_price_max || 0}
          onChange={e => handleChangeProductInfo('sale_price_max', e.target.value)}
          fullWidth
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
              inputComponent: asInputComponent(MoneyInput),
            },
          }}
        />

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleValidation}>
            {type === 'new' ? 'Adicionar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Container>
    </Dialog>
  );
};

export default SupplierProductForm;
